import React, { type ReactNode } from "react";
import { useTranslation } from "next-i18next";
import cx from "classnames";

import { Button } from "common/ui/Button";
import { Icon } from "common/ui/Icon";
import { Heading } from "common/ui/Heading";
import { ErrorDisplay } from "common/ui/ErrorDisplay";
import { ButtonWithOptions } from "common/ui/ButtonWithOptions";

import styles from "./Modal.module.scss";

interface ModalOverlayProps {
  children: ReactNode;
  close?: () => any;
  title: string;
  primaryAction?: {
    text: string,
    onClick:() => void,
    type?: "destructive",
    isDisabled?: boolean,
    actions?: {
      name: string | number,
      disabled?: boolean,
      onClick: () => void,
    }[],
  };
  secondaryAction?: {
    text: string,
    onClick:() => void,
    isDisabled?: boolean,
  };
  tertiaryAction?: {
    variant: "primary" | "secondary" | "destructive" | "secondaryDestructive",
    text: string,
    onClick:() => void,
    isDisabled?: boolean,
  };
  destructiveAction?: { text: string, onClick:() => void };
  footer?: ReactNode,
  isProcessing?: boolean;
  error?: string;
  removeBodyPadding?: boolean;
  className?: string,
}

export interface ModalProps extends ModalOverlayProps {
  banner?: React.FC<{ children: ReactNode, className: string }>;
}

export function Modal({
  banner: Banner,
  ...rest
}: ModalProps) {
  return (
    <div className={styles.Modal}>
      {Banner ? (
        <Banner className={styles.ModalBanner}>
          <ModalOverlay {...rest} />
        </Banner>
      ) : (
        <ModalOverlay {...rest} />
      )}
    </div>
  );
}

const ModalOverlay = ({
  children,
  close,
  title,
  primaryAction,
  secondaryAction,
  tertiaryAction,
  destructiveAction,
  footer,
  isProcessing,
  error,
  removeBodyPadding,
  className
}: ModalOverlayProps) => {
  const { t } = useTranslation("common");
  return (
    <div className={`${styles.ModalOverlay} ${className}`}>
      <div className={styles.Header}>
        <Heading variant="h2">{title}</Heading>
        {close && (
          <div className={styles.Icon}>
            <Icon
              icon="closeMajor"
              onClick={() => close()}
            />
          </div>
        )}
      </div>
      <div className={cx(styles.ModalBody, {
        [styles.noPadding]: removeBodyPadding,
      })}>
        {children}
      </div>
      <div className={styles.Footer}>
        {footer && footer}
        {error && <ErrorDisplay error={error} />}
        {(primaryAction || secondaryAction || tertiaryAction) ? (
          <div className={cx(styles.Buttons, styles.Primary)}>
            {primaryAction && (
              primaryAction.actions ? (
                <ButtonWithOptions
                  disabled={isProcessing || primaryAction.isDisabled}
                  primaryAction={{
                    title: primaryAction.text,
                    onClick: primaryAction.onClick
                  }}
                  actions={primaryAction.actions}
                  alignTop
                />
              ) : (
                <Button
                  disabled={isProcessing || primaryAction.isDisabled}
                  variant={primaryAction.type === "destructive"
                    ? "destructive"
                    : "primary"}
                  onClick={primaryAction.onClick}
                >
                  {primaryAction.text}
                </Button>
              )
            )}
            {secondaryAction && (
              <Button
                disabled={isProcessing || secondaryAction.isDisabled}
                variant="secondary"
                onClick={secondaryAction.onClick}
              >
                {secondaryAction.text}
              </Button>
            )}
            {tertiaryAction && (
              <Button
                variant={tertiaryAction.variant}
                onClick={tertiaryAction.onClick}
                disabled={isProcessing || tertiaryAction.isDisabled}
              >
                {tertiaryAction.text}
              </Button>
            )}
          </div>
        ) : destructiveAction && secondaryAction ? (
          <div className={cx(styles.Buttons, styles.Destructive)}>
            <Button
              disabled={isProcessing}
              variant="secondaryDestructive"
              onClick={destructiveAction.onClick}
            >
              {destructiveAction.text}
            </Button>
            <Button
              disabled={isProcessing}
              variant="secondary"
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.text}
            </Button>
          </div>
        ) : (
          <Button variant="secondary" onClick={close}>
            {t("ui.modal.done")}
          </Button>
        )}
      </div>
    </div>
)}

